/**
 * Party form
 * Handles primary/secondary role selection for party listings and the party-only parts of the form:
 * the rank in the summoner card and the preview
 */

import { t } from '../../i18n';
import { byId, all, setText } from '../../utils/dom';

const PartyFormStrategy = {
    /**
     * Initialize state for party roles
     */
    initState(form) {
        form.primaryRoleId = null;
        form.secondaryRoleId = null;
    },


    /**
     * Handle role button click
     */
    handleRoleClick(form, btn) {
        const roleId = Number(btn.dataset.roleId);

        if (roleId === form.primaryRoleId) {
            // Click on primary -> remove it
            form.primaryRoleId = null;
        } else if (roleId === form.secondaryRoleId) {
            // Click on secondary -> remove it
            form.secondaryRoleId = null;
        } else if (!form.primaryRoleId) {
            // No primary -> set as primary
            form.primaryRoleId = roleId;
        } else if (!form.secondaryRoleId) {
            // Has primary, no secondary -> set as secondary
            form.secondaryRoleId = roleId;
        } else {
            // Both set -> replace secondary
            form.secondaryRoleId = roleId;
        }

        this.render(form);
    },

    /**
     * Render roles UI
     */
    render(form) {
        all('.pf-role').forEach(function(btn) {
            const roleId = Number(btn.dataset.roleId);
            const isPrimary = roleId === form.primaryRoleId;
            const isSecondary = roleId === form.secondaryRoleId;

            btn.classList.toggle('pf-role--primary', isPrimary);
            btn.classList.toggle('pf-role--secondary', isSecondary);
            const badge = btn.querySelector('.pf-role-badge');
            if (badge) badge.textContent = isPrimary ? t('Основная') : isSecondary ? t('Доп.') : '';
        });

        // Update hidden inputs
        form.setInput('primary_role_id', form.primaryRoleId || '');
        form.setInput('secondary_role_id', form.secondaryRoleId || '');

        // Update dynamic hint
        this.updateHint(form);

        // Hide role error if primary is now selected
        if (form.primaryRoleId) {
            this.showError(false);
        }

        // Update footer buttons
        form.updatePublishButton();

        // Update preview roles
        this.renderPreviewRoles(form);
    },

    /**
     * Update roles hint based on selection state
     */
    updateHint(form) {
        const hint = byId('pf-roles-hint');
        if (!hint) return;
        let text;

        if (!form.primaryRoleId && !form.secondaryRoleId) {
            text = t('Выберите основную роль');
        } else if (form.primaryRoleId && !form.secondaryRoleId) {
            text = t('Выберите дополнительную или кликните на выбранную для отмены');
        } else {
            text = t('Кликните на роль для отмены выбора');
        }

        hint.textContent = text;
    },

    /**
     * Show or hide the "pick a primary role" error and highlight the roles
     */
    showError(show) {
        byId('pf-roles-error')?.classList.toggle('hidden', !show);
        byId('pf-roles-hint')?.classList.toggle('hidden', show);
        all('.pf-roles').forEach((el) => el.classList.toggle('pf-roles--error', show));
    },

    /**
     * Validate that primary role is selected
     */
    validate(form) {
        this.showError(!form.primaryRoleId);

        if (!form.primaryRoleId) {
            document.querySelector('.pf-roles')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }

        return !!form.primaryRoleId;
    },

    /**
     * Rank block of the summoner card, for ranked queues
     */
    onSummonerFound(form, data) {
        const rankBlock = byId('pf-rank-block');
        if (data.is_ranked) {
            rankBlock.querySelector('.pf-sum-rank-img').src = data.rank.icon_url;
            rankBlock.querySelector('.pf-sum-queue').textContent = data.queue_name;
            rankBlock.querySelector('.pf-sum-rank').textContent = data.rank.display_text;
        }
        rankBlock.classList.toggle('hidden', !data.is_ranked);
    },

    /**
     * Preview card: level, rank and roles (the promote form renders the party's roles on the server)
     */
    renderPreview(form, data) {
        setText('pf-pv-level', data.level);
        const rankImg = byId('pf-pv-rank-img');
        if (data.rank?.icon_url && rankImg) {
            rankImg.src = data.rank.icon_url;
        }
        setText('pf-pv-rank-text', data.rank?.display_text || '');

        if (!form.isPromote) {
            this.renderPreviewRoles(form);
        }
    },

    /**
     * Update preview roles from selected roles
     */
    renderPreviewRoles(form) {
        const rolesContainer = byId('pf-pv-roles');
        if (!rolesContainer) return;

        // Icon and name of the primary role, then " + " and the secondary one
        const roleHtml = (roleId) => {
            const role = roleId && document.querySelector(`.pf-role[data-role-id="${roleId}"]`);
            const slug = role?.dataset.roleSlug;
            const name = role?.querySelector('.pf-role-name')?.textContent;
            return slug && name
                ? `<span class="pf-pv-role-icon"><img src="${form.roleIconUrl(slug)}" alt=""></span>${form.escapeHtml(name)}`
                : '';
        };
        const primary = roleHtml(form.primaryRoleId);
        const secondary = roleHtml(form.secondaryRoleId);
        rolesContainer.innerHTML = primary + (secondary ? ' + ' + secondary : '');

        // Show/hide dot separator based on whether we have roles
        byId('pf-pv-dot-sep')?.classList.toggle('hidden', !form.primaryRoleId);
    }
};

export default PartyFormStrategy;
